import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import { API_NETWORK_SPEED } from '@/service/url';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { getUserAccessToken } from '@/utilities/setting';

/**
 * Internet speed of the logged-in agent / team lead, for the CTI header.
 * Every 10 seconds it times a small download (GET) and upload (POST) against the backend
 * (`/v2/network-speed`) and rates each direction for a voice call:
 *   Poor < 64 kbps · Moderate 64–80 kbps · Good ≥ 80 kbps (per direction).
 * Runs during calls too — a bad line is exactly when the agent needs to see it.
 * Failures never toast or log the user out; they only change what the header shows.
 */

export type SpeedLevel = 'good' | 'moderate' | 'poor' | 'unknown';

export const SPEED_KBPS = { moderateFrom: 64, goodFrom: 80 } as const;

// Small payloads, both directions at the same time, so a reading takes well under a second.
const DOWNLOAD_BYTES = 16 * 1024;
const UPLOAD_BYTES = 8 * 1024;
const TIMEOUT_MS = 10_000;
const DEFAULT_INTERVAL_MS = 10_000;
// When a test cannot run, try again quickly instead of waiting a full interval.
const RETRY_MS = 3_000;
const MAX_QUICK_RETRIES = 10;

export interface NetworkSpeed {
  /** kbps, null until measured or when the test could not run. */
  downKbps: number | null;
  upKbps: number | null;
  down: SpeedLevel;
  up: SpeedLevel;
  /** Worse of the two directions. */
  level: SpeedLevel;
  offline: boolean;
  measuredAt: number | null;
}

const ORDER: Record<SpeedLevel, number> = { unknown: 0, good: 1, moderate: 2, poor: 3 };

export function levelFor(kbps: number | null): SpeedLevel {
  if (kbps === null) return 'unknown';
  if (kbps < SPEED_KBPS.moderateFrom) return 'poor';
  if (kbps < SPEED_KBPS.goodFrom) return 'moderate';
  return 'good';
}

export function worseLevel(a: SpeedLevel, b: SpeedLevel): SpeedLevel {
  return ORDER[a] >= ORDER[b] ? a : b;
}

export function formatKbps(kbps: number | null): string {
  if (kbps === null) return '—';
  if (kbps >= 1000) return `${(kbps / 1000).toFixed(kbps >= 10_000 ? 0 : 1)} Mbps`;
  return `${Math.round(kbps)} kbps`;
}

/** 'failed' = the test could not run (endpoint missing, auth); null kbps on timeout = too slow. */
type Probe = { kbps: number } | 'failed';

function headers(): Record<string, string> {
  const token = getUserAccessToken();
  return {
    ...(token ? { Authorization: String(token) } : {}),
    'x-client-source': CLIENT_SOURCE,
  };
}

/**
 * Network time of one request from the browser's Resource Timing: from the moment the request
 * actually left (after waiting for a free connection — busy right after login) to the end of the
 * response. A PerformanceObserver is used because the timing buffer is often already full.
 * Needs the backend's Timing-Allow-Origin header; null when not available.
 */
function watchTiming(url: string): { read: () => Promise<number | null>; stop: () => void } {
  let entry: PerformanceResourceTiming | null = null;
  let observer: PerformanceObserver | null = null;
  try {
    observer = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.name === url) entry = e as PerformanceResourceTiming;
      }
    });
    observer.observe({ type: 'resource', buffered: false });
  } catch {
    observer = null;
  }
  const stop = () => observer?.disconnect();
  const read = async () => {
    // The entry is delivered just after the response completes.
    for (let i = 0; i < 5 && !entry; i++) await new Promise((r) => window.setTimeout(r, 20));
    const e = entry as PerformanceResourceTiming | null;
    if (!e || !e.requestStart || !e.responseEnd || e.responseEnd <= e.requestStart) return null;
    return e.responseEnd - e.requestStart;
  };
  return { read, stop };
}

async function timed(url: string, init: RequestInit, bytes: number): Promise<Probe> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), TIMEOUT_MS);
  const timing = watchTiming(url);
  const started = performance.now();
  try {
    const res = await fetch(url, { ...init, cache: 'no-store', signal: controller.signal });
    if (!res.ok) return 'failed';
    await res.arrayBuffer();
    const wallMs = performance.now() - started;
    const networkMs = await timing.read();
    const ms = Math.max(1, networkMs ?? wallMs);
    return { kbps: (bytes * 8) / ms }; // bits per ms = kbps
  } catch {
    // Timed out (or the connection dropped mid-test): at most this fast.
    if (controller.signal.aborted) return { kbps: (bytes * 8) / TIMEOUT_MS };
    return 'failed';
  } finally {
    window.clearTimeout(timer);
    timing.stop();
  }
}

// Random bytes (not compressible), created once.
let uploadPayload: Blob | null = null;
function payload(): Blob {
  if (!uploadPayload) {
    const bytes = new Uint8Array(new ArrayBuffer(UPLOAD_BYTES));
    crypto.getRandomValues(bytes);
    uploadPayload = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/octet-stream' });
  }
  return uploadPayload;
}

async function measure(): Promise<{ downKbps: number | null; upKbps: number | null }> {
  const base = `${BACKEND_API_BASE_URL}${API_NETWORK_SPEED}`;
  // Unique URLs so each request's timing entry can be found.
  const stamp = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const [down, up] = await Promise.all([
    timed(`${base}?bytes=${DOWNLOAD_BYTES}&t=${stamp}`, { headers: headers() }, DOWNLOAD_BYTES),
    timed(
      `${base}?t=${stamp}`,
      {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/octet-stream' },
        body: payload(),
      },
      UPLOAD_BYTES,
    ),
  ]);
  return {
    downKbps: down === 'failed' ? null : down.kbps,
    upKbps: up === 'failed' ? null : up.kbps,
  };
}

const INITIAL: NetworkSpeed = {
  downKbps: null,
  upKbps: null,
  down: 'unknown',
  up: 'unknown',
  level: 'unknown',
  offline: false,
  measuredAt: null,
};

// One reading for the header and the dialer. The header runs the test; the
// dialer only reads this, so login and Call stay in step with the latest sample.
let snapshot: NetworkSpeed = INITIAL;
const listeners = new Set<() => void>();

function publish(next: NetworkSpeed) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

export function getNetworkSpeed(): NetworkSpeed {
  return snapshot;
}

export function isNetworkPoor(): boolean {
  return snapshot.level === 'poor';
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useNetworkSpeedState(): NetworkSpeed {
  return useSyncExternalStore(subscribe, getNetworkSpeed, getNetworkSpeed);
}

export const POOR_WEBRTC_LOGIN_MESSAGE =
  'WebRTC login is unavailable because your internet is too slow (under 64 kbps). Use Mobile, or try again when the connection improves.';

export function toastPoorWebrtcCall() {
  const speed = snapshot;
  const detail = speed.offline
    ? 'You are offline. A WebRTC call needs at least 64 kbps.'
    : `Upload ${formatKbps(speed.upKbps)} · Download ${formatKbps(speed.downKbps)}. A WebRTC call needs at least ${SPEED_KBPS.moderateFrom} kbps each way.`;
  toast.error('Call blocked — internet speed is too low', {
    id: 'network-speed-call',
    description: detail,
    duration: 8_000,
  });
}

function toSpeed(downKbps: number | null, upKbps: number | null, offline: boolean): NetworkSpeed {
  if (offline) {
    return { downKbps: 0, upKbps: 0, down: 'poor', up: 'poor', level: 'poor', offline: true, measuredAt: Date.now() };
  }
  const down = levelFor(downKbps);
  const up = levelFor(upKbps);
  return { downKbps, upKbps, down, up, level: worseLevel(down, up), offline: false, measuredAt: Date.now() };
}

export function useNetworkSpeed(intervalMs: number = DEFAULT_INTERVAL_MS): NetworkSpeed {
  const [speed, setSpeed] = useState<NetworkSpeed>(snapshot);
  const running = useRef(false);
  const apply = (next: NetworkSpeed) => {
    publish(next);
    setSpeed(next);
  };

  useEffect(() => {
    let alive = true;
    let retryTimer: number | undefined;
    let failedInARow = 0;

    const run = async () => {
      if (running.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        apply(toSpeed(null, null, true));
        return;
      }
      running.current = true;
      try {
        const { downKbps, upKbps } = await measure();
        if (!alive) return;
        if (downKbps === null && upKbps === null) {
          // Test could not run (e.g. right after login): keep the last reading and retry soon
          // instead of waiting for the next interval.
          failedInARow += 1;
          if (failedInARow <= MAX_QUICK_RETRIES) {
            window.clearTimeout(retryTimer);
            retryTimer = window.setTimeout(() => void run(), RETRY_MS);
          } else {
            // Still nothing after the quick retries: stop showing "checking".
            if (!snapshot.measuredAt) apply(toSpeed(null, null, false));
          }
          return;
        }
        failedInARow = 0;
        apply(toSpeed(downKbps, upKbps, false));
      } finally {
        running.current = false;
      }
    };

    void run();
    const timer = window.setInterval(() => void run(), intervalMs);
    const onOnline = () => void run();
    const onOffline = () => apply(toSpeed(null, null, true));
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.clearTimeout(retryTimer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [intervalMs]);

  return speed;
}
